import type { FC } from 'react';
import { useEffect } from 'react';

import { t } from '@luna/i18n';

import { useCurrentRoute } from '../useCurrentRoute';
import { normalizePath } from '../normalizePath';
import { usePageTitleOverride } from './PageTitleOverride';

export function getTranslationFallbacks(
    currentRoute: string,
    defaultTitle: string,
    translate = t,
): [string, string][] {
    let routeTranslationKey = `title.${currentRoute.replaceAll(':', '_')}`;
    const translationFallbacks: [string, string][] = [];
    translationFallbacks.push([routeTranslationKey, translate(routeTranslationKey)]);
    let slashIndex = routeTranslationKey.lastIndexOf('/');
    while (slashIndex !== -1) {
        routeTranslationKey = routeTranslationKey.substring(0, slashIndex);
        translationFallbacks.push([routeTranslationKey, translate(routeTranslationKey)]);
        slashIndex = routeTranslationKey.lastIndexOf('/');
    }
    translationFallbacks.push(['default', defaultTitle]);

    return translationFallbacks;
}

export function getTitleTranslation(
    currentRoute: string,
    defaultTitle: string,
    translate = t,
): string {
    return (
        getTranslationFallbacks(currentRoute, defaultTitle, translate).find(([key, value]) => {
            return key !== value;
        })?.[1] ?? ''
    );
}

interface Props {
    defaultTitle: string;
}

export const PageTitleHandler: FC<Props> = ({ defaultTitle }) => {
    const currentRoute = useCurrentRoute();
    const { title: titleOverride } = usePageTitleOverride();

    useEffect(() => {
        const titleNode = document.querySelector('title');
        if (!titleNode) {
            return;
        }

        titleNode.innerText =
            titleOverride ?? getTitleTranslation(normalizePath(currentRoute), defaultTitle);
    }, [currentRoute, defaultTitle, titleOverride]);

    return null;
};
